import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import {
  BaseClickable,
  Box,
  Link,
  palette,
  Spinner,
  Stack,
  style,
  Text,
} from '@solvhealth/jigsaw-v1';
import Location from '@solvhealth/types/interfaces/Location';
import Slot from '@solvhealth/types/interfaces/Location/Slot';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { batch, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { setActiveLocation } from '~/actions/location';
import {
  bookingTimeSelected,
  setAppointmentType,
  setBookingIsPremium,
  setBookingIsPremiumInPerson,
  setBookingStateCode,
  setLocationId,
} from '~/actions/newBooking';
import { useLocationSlots } from '~/components/CareTeam/hooks/useSlots';
import { HOUR_MINUTE_PERIOD } from '~/components/DateTime/DateTime';
import { usePushToPremiumBookingFlow } from '~/components/Membership/utils';
import { getImageObject } from '~/components/SolvPatternLibrary/Picture/util';
import { PICK_TIME_VALUE } from '~/constants';
import {
  PREMIUM_BOOKING_CARD_SAVE_ERROR,
  PREMIUM_IN_PERSON_BOOKING_FLOW,
  PREMIUM_IN_PERSON_SELECT_PREVIEW_TIME_SLOT,
  PREMIUM_IN_PERSON_SELECT_SEE_MORE_TIMES,
} from '~/core/analytics/events';
import { getAppendedTimeZoneInfo } from '~/core/util/date';
import {
  formatCityStateZip,
  formatDisplayStreetAddress,
  formatLocationName,
} from '~/core/util/location';
import { setCurrentlySelectedLocation } from '~/ducks/currentlySelectedLocation';

const LocationImage = styled.img.withConfig({ componentId: 'sc-1d85kxi-0' })(
  style({
    borderRadius: 'full',
    overflow: 'hidden',
    objectFit: 'fill',
    flexShrink: 0,
  })
);

type Props = { location: Location; onSelect?: () => void };

export const PremiumLocationCard = ({ location, onSelect }: Props) => {
  const { isLoading, data } = useLocationSlots(location.id);
  const dispatch = useDispatch();

  const slots = data?.data;

  const image = getImageObject(location, { imageIndex: 0, showDefaultPictureIfMissing: true });

  const { t } = useTranslation(['membership', 'common']);
  const push = usePushToPremiumBookingFlow();

  const handleSlotSelect = (slot: Slot) => {
    const timeSlot = moment(slot.appointmentDate)
      .tz(location.timeZone as string)
      .valueOf();
    batch(() => {
      dispatch(setBookingIsPremium(true));
      dispatch(setBookingIsPremiumInPerson(true));
      dispatch(setLocationId(location.id));
      dispatch(setBookingStateCode(location.state));
      dispatch(setActiveLocation(location));
      dispatch(setCurrentlySelectedLocation(location));
      dispatch(setAppointmentType(PICK_TIME_VALUE));
      dispatch(bookingTimeSelected(timeSlot, location.timeZone as string));

      push({ startFrom: 'profile' });
    });
    onSelect?.();
  };

  const handleSeeMore = () => {
    batch(() => {
      dispatch(setBookingIsPremium(true));
      dispatch(setBookingIsPremiumInPerson(true));
      dispatch(setLocationId(location.id));
      dispatch(setBookingStateCode(location.state));
      dispatch(setActiveLocation(location));
      dispatch(setCurrentlySelectedLocation(location));

      push({ startFrom: 'time' });
    });
    onSelect?.();
  };

  const formatSlot = (slot: Slot) => {
    const momentObj = moment(slot.appointmentDate).tz(location.timeZone);
    return `${momentObj.format(HOUR_MINUTE_PERIOD)}${getAppendedTimeZoneInfo(location.timeZone)}`;
  };

  return (
    <Stack space={1}>
      <Stack row space={1}>
        <LocationImage height={44} src={image.fullRes} width={44} />
        <Stack alignItems="flex-start" space={0.5}>
          {location.isPediatricsOnly && (
            <Box bg="peach-light" borderRadius="base" flexShrink={0} px={1} py={0.5}>
              <Text textColor="peach-dark" variant="subtitle2">
                {t('membership:inPerson.pediatricOnly')}
              </Text>
            </Box>
          )}
          <Stack>
            <Text variant="subtitle">{formatLocationName(location)}</Text>
            <Text muted>{formatDisplayStreetAddress(location)}</Text>
            <Text muted>{formatCityStateZip(location)}</Text>
          </Stack>
        </Stack>
      </Stack>
      <Stack alignItems="center" row space={1}>
        {isLoading && <Spinner />}
        {!isLoading && isEmpty(slots?.slots) ? (
          <Text muted>
            {t('membership:premiumInPersonLocationSelector.noTimesAvailable')}{' '}
            <Link inline onClick={handleSeeMore}>
              {t('membership:premiumInPersonLocationSelector.seeOtherDays')}
            </Link>
          </Text>
        ) : (
          <Stack maxWidth="100%">
            <Stack position="relative">
              {!isEmpty(slots?.slots) && (
                <Text muted variant="body2">
                  Today
                </Text>
              )}
              <Stack
                alignItems="center"
                maxWidth="100%"
                overflowX="scroll"
                position="relative"
                py={1}
                row
                space={1}
              >
                {slots?.slots.slice(0, 8).map((slot) => (
                  <BaseClickable
                    bg="crunchberry-xlight"
                    borderRadius="base"
                    disabled={slot.availability < 1}
                    event={PREMIUM_IN_PERSON_BOOKING_FLOW}
                    flexShrink={0}
                    key={slot.appointmentDate}
                    meta={{
                      action: PREMIUM_IN_PERSON_SELECT_PREVIEW_TIME_SLOT,
                      slot: formatSlot(slot),
                    }}
                    onClick={() => handleSlotSelect(slot)}
                    px={2}
                    py={1}
                    sx={{ ':disabled': { bg: 'gray-200', color: 'gray-300' } }}
                    textColor="crunchberry-dark"
                  >
                    <Text fontSize="sm" style={{ color: 'currentcolor' }} variant="button">
                      {formatSlot(slot)}
                    </Text>
                  </BaseClickable>
                ))}
              </Stack>
              <Box
                bottom={0}
                position="absolute"
                right={0}
                style={{
                  background: 'linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,1))',
                }}
                top={0}
                width={50}
              />
            </Stack>
            {!isLoading && (
              <Link
                event={PREMIUM_IN_PERSON_BOOKING_FLOW}
                meta={{ action: PREMIUM_IN_PERSON_SELECT_SEE_MORE_TIMES }}
                onClick={() => handleSeeMore()}
              >
                <Stack justifyContent="space-between" row width="100%">
                  {t('common:seeMore')}
                  <ChevronRightIcon fontSize="small" htmlColor={palette.crunchberry.medium} />
                </Stack>
              </Link>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
